// Cart Drawer Main JavaScript
class CartDrawer {
  constructor() {
    this.initializeSelectors();
    this.initializeEventListeners();
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.closeButton = document.querySelector('.cart-drawer-close');
    this.initializeCloseEvents();

    // Add listeners for cart updates
    document.addEventListener('cart:added', async (event) => {
      try {
        await this.refreshCart(event.detail.cartData);
      } catch (error) {
        console.error('Error handling cart:added event:', error);
      }
    });

    // Initial cart fetch
    this.refreshCart().catch((error) => {
      console.error('Error during initial cart fetch:', error);
      this.showError('Failed to load cart. Please refresh the page.');
    });
  }

  initializeSelectors() {
    // Cart drawer elements
    this.drawer = document.querySelector('.cart-drawer');
    this.drawerItems = document.querySelector('.cart-drawer-items');
    this.drawerTotal = document.querySelector('.cart-drawer-total');
    this.checkoutButton = document.querySelector('.checkout-button');
    this.cartTriggers = document.querySelectorAll('.cart-trigger');
    this.cartCountBadges = document.querySelectorAll('.cart-count');
  }

  initializeCloseEvents() {
    // Close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeDrawer());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeDrawer());
    }

    // ESC key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isDrawerOpen()) {
        this.closeDrawer();
      }
    });
  }

  closeDrawer() {
    if (this.drawer) {
      this.drawer.classList.remove('active');
      this.drawer.classList.add('translate-x-full');

      // Hide drawer after animation completes
      setTimeout(() => {
        this.drawer.classList.add('hidden');
      }, 300); // Match this with your CSS transition duration
    }

    if (this.overlay) {
      this.overlay.classList.add('hidden');
    }

    // Enable page scrolling
    document.body.style.overflow = '';
  }

  openDrawer() {
    if (this.drawer) {
      this.drawer.classList.remove('hidden');
      // Force a reflow to ensure the transition works
      this.drawer.offsetHeight;
      this.drawer.classList.add('active');
      this.drawer.classList.remove('translate-x-full');
    }

    if (this.overlay) {
      this.overlay.classList.remove('hidden');
    }

    // Prevent page scrolling when drawer is open
    document.body.style.overflow = 'hidden';
  }

  isDrawerOpen() {
    return this.drawer && this.drawer.classList.contains('active');
  }

  initializeEventListeners() {
    // Initialize Add to bag buttons
    document.querySelectorAll('.add-to-cart-button').forEach((button) => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });

    // Initialize cart triggers (icons)
    this.cartTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.openDrawer.bind(this));
    });

    // Initialize checkout button
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', this.handleCheckout.bind(this));
    }

    // Initialize cart item controls
    this.initializeCartItemControls();
  }

  initializeCartItemControls() {
    document.querySelectorAll('.cart-item').forEach((item) => {
      const itemId = item.dataset.itemId;
      const quantityInput = item.querySelector('input[type="number"]');
      const decreaseBtn = item.querySelector('.quantity-decrease');
      const increaseBtn = item.querySelector('.quantity-increase');
      const removeBtn = item.querySelector('.remove-item');

      if (quantityInput) {
        quantityInput.addEventListener('change', () => this.updateQuantity(itemId, quantityInput.value));
      }

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const newQty = Math.max(1, parseInt(quantityInput.value) - 1);
          quantityInput.value = newQty;
          this.updateQuantity(itemId, newQty);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          const newQty = parseInt(quantityInput.value) + 1;
          quantityInput.value = newQty;
          this.updateQuantity(itemId, newQty);
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', () => this.removeItem(itemId));
      }
    });
  }

  getErrorMessage(error) {
    const commonErrors = {
      'Not enough inventory': 'Sorry, not enough items in stock',
      'Invalid quantity': 'Please enter a valid quantity',
      'Cannot ship to this destination': 'We cannot ship to this location',
      'Product is sold out': 'This product is currently sold out',
    };

    // Check if it's a known error
    for (const [key, message] of Object.entries(commonErrors)) {
      if (error.message.includes(key)) {
        return message;
      }
    }

    // Return a generic error if we don't recognize it
    return 'There was a problem adding this item to your cart. Please try again.';
  }

  async handleAddToCart(event) {
    event.preventDefault();

    const form = event.target.closest('form');
    if (!form) {
      this.showError('Could not find product form');
      return;
    }

    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    if (isNaN(quantity) || quantity < 1) {
      this.showError('Please enter a valid quantity');
      return;
    }

    this.toggleButtonLoading(button, true);

    try {
      const response = await this.addToCart(productId, quantity);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to bag failed');
      }

      const cartData = await response.json();
      await this.refreshCart(cartData);
      this.openDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(this.getErrorMessage(error));
    } finally {
      this.toggleButtonLoading(button, false);
    }
  }

  async updateQuantity(itemId, newQuantity) {
    const cartItem = document.querySelector(`[data-item-id="${itemId}"]`);
    if (!cartItem) return;

    cartItem.classList.add('opacity-50');

    try {
      const response = await this.changeCart(itemId, parseInt(newQuantity));
      if (!response.ok) throw new Error('Failed to update quantity');

      const cartData = await response.json();
      await this.refreshCart(cartData);
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError('Failed to update quantity');
      this.revertQuantityInput(cartItem);
    } finally {
      cartItem.classList.remove('opacity-50');
    }
  }

  async removeItem(itemId) {
    const cartItem = document.querySelector(`[data-item-id="${itemId}"]`);
    if (!cartItem) return;

    cartItem.classList.add('opacity-50');

    try {
      const response = await this.changeCart(itemId, 0);
      if (!response.ok) throw new Error('Failed to remove item');

      const cartData = await response.json();
      this.animateItemRemoval(cartItem);
      await this.refreshCart(cartData);
    } catch (error) {
      console.error('Error removing item:', error);
      this.showError('Failed to remove item');
      cartItem.classList.remove('opacity-50');
    }
  }

  async handleCheckout() {
    if (!this.checkoutButton) return;

    const spinner = this.checkoutButton.querySelector('.loading-spinner');
    const buttonText = this.checkoutButton.querySelector('span');

    try {
      this.checkoutButton.disabled = true;
      if (spinner) spinner.classList.remove('hidden');
      if (buttonText) buttonText.textContent = 'Redirecting...';

      const cart = await this.fetchCart();
      if (cart.item_count === 0) throw new Error('Cart is empty');

      window.location.href = '/checkout';
    } catch (error) {
      console.error('Checkout error:', error);
      this.showError('Unable to proceed to checkout');

      this.checkoutButton.disabled = false;
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.textContent = 'Checkout';
    }
  }

  async addToCart(productId, quantity = 1) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: productId,
            quantity: parseInt(quantity, 10),
          },
        ],
      }),
    });
  }

  showError(message) {
    const existingError = document.querySelector('.cart-error-message');
    if (existingError) {
      existingError.remove();
    }

    const errorDiv = document.createElement('div');
    errorDiv.className =
      'cart-error-message fixed top-4 right-4 bg-red-500 text-white px-4 py-3 rounded shadow-lg z-50 animate-fade-in flex items-center';

    errorDiv.innerHTML = `
      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>${message}</span>
      <button class="ml-4 hover:text-gray-200" onclick="this.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `;

    document.body.appendChild(errorDiv);

    setTimeout(() => {
      if (errorDiv.isConnected) {
        errorDiv.remove();
      }
    }, 5000);
  }

  async changeCart(itemId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemId, quantity }),
    });
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw new Error('Failed to fetch cart data');
    }
  }

  async refreshCart(cartData = null) {
    try {
      // console.log('Initial cartData:', cartData);

      // If we received cartData from an add operation, it only contains items
      // So we need to fetch the full cart data
      if (cartData && cartData.items && !cartData.item_count) {
        // console.log('Fetching complete cart data after add operation');
        cartData = await this.fetchCart();
      }

      // If cartData wasn't provided at all, fetch it
      if (!cartData) {
        // console.log('No cartData provided, fetching from /cart.js');
        cartData = await this.fetchCart();
      }

      // console.log('Final cart data:', cartData);

      // Calculate total items
      const itemCount = cartData.items.reduce((total, item) => total + item.quantity, 0);
      // console.log('Calculated item count:', itemCount);

      // Update cart count badges
      this.updateCartCounts(itemCount);

      // Update all cart totals on the page
      const cartTotalElements = document.querySelectorAll('[data-cart-total]');
      cartTotalElements.forEach((element) => {
        const formattedTotal = this.formatCartMoney(cartData.total_price);
        element.textContent = formattedTotal;
      });

      // Rest of the method remains the same...
      const drawerResponse = await fetch('/?section_id=cart-drawer');
      const html = await drawerResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartItems = doc.querySelector('.cart-drawer-items');

      if (newCartItems && this.drawerItems) {
        this.drawerItems.innerHTML = newCartItems.innerHTML;
        this.initializeCartItemControls();
        // console.log('Cart drawer content updated');
      }

      if (itemCount === 0) {
        this.showEmptyCartMessage();
      }

      // Update checkout button state
      const checkoutButton = document.querySelector('.checkout-button');
      if (checkoutButton) {
        checkoutButton.disabled = itemCount === 0;
      }
    } catch (error) {
      console.error('Detailed error in refreshCart:', {
        error: error.message,
        stack: error.stack,
      });
      this.showError('Failed to update cart. Please refresh the page.');
    }
  }

  formatCartMoney(cents) {
    // Get the format from an existing cart total element to extract currency symbol
    const cartTotalElement = document.querySelector('[data-cart-total]');
    if (!cartTotalElement) return `€${(cents / 100).toFixed(2)}`;

    const baseFormat = cartTotalElement.textContent;
    const currencySymbol = baseFormat
      .replace(/[0-9.,]/g, '')
      .trim()
      .split(' ')[0];

    // Convert cents to decimal and format with 2 decimal places
    const amount = (cents / 100).toFixed(2);

    // Return in €XX.XX format
    return `${currencySymbol}${amount}`;
  }

  updateCartCounts(count) {
    // console.log('updateCartCounts called with count:', count);

    // Ensure count is a number and not undefined
    count = parseInt(count) || 0;

    // First, find all existing cart count badges
    this.cartCountBadges = document.querySelectorAll('.cart-count');
    // console.log('Found cart badges:', this.cartCountBadges.length);

    // If there are no badges but count > 0, create them for each cart trigger
    if (this.cartCountBadges.length === 0 && count > 0) {
      // console.log('Creating new cart badges');
      document.querySelectorAll('.cart-trigger').forEach((trigger) => {
        const newBadge = document.createElement('span');
        newBadge.className =
          'cart-count absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center';
        trigger.appendChild(newBadge);
      });
      // Re-query the badges after creating them
      this.cartCountBadges = document.querySelectorAll('.cart-count');
      // console.log('New badges created, count:', this.cartCountBadges.length);
    }

    // Update all badges
    this.cartCountBadges.forEach((badge) => {
      if (count > 0) {
        badge.textContent = count;
        badge.classList.remove('hidden');
        this.animateCountBadge(badge);
        // console.log('Badge updated with count:', count);
      } else {
        badge.classList.add('hidden');
        // console.log('Badge hidden due to zero count');
      }
    });
  }

  toggleButtonLoading(button, isLoading) {
    const textElement = button.querySelector('.add-to-cart-text');
    const spinnerElement = button.querySelector('.loading-spinner');

    if (isLoading) {
      // Store the current button content before changing to loading state
      this.previousButtonContent = textElement.innerHTML;
      textElement.textContent = 'Adding...';
      spinnerElement?.classList.remove('hidden');
      button.disabled = true;
    } else {
      // Let the ProductForm handle the reset state
      const form = button.closest('form');
      if (form) {
        const productFormEl = form.closest('.product-form');
        if (productFormEl) {
          const productForm = [...document.querySelectorAll('.product-form')].find(
            (el) => el === productFormEl
          ).__productForm;
          if (productForm) {
            productForm.toggleLoadingState(false);
            return;
          }
        }
      }

      // Fallback if ProductForm instance not found
      spinnerElement?.classList.add('hidden');
      button.disabled = false;
    }
  }

  animateCountBadge(badge) {
    badge.animate(
      [
        { transform: 'scale(1.2)', opacity: '0.8' },
        { transform: 'scale(1)', opacity: '1' },
      ],
      {
        duration: 300,
        easing: 'ease-out',
      }
    );
  }

  animateItemRemoval(item) {
    item.style.height = item.offsetHeight + 'px';
    item.style.transition = 'all 0.3s ease-out';

    setTimeout(() => {
      item.style.height = '0';
      item.style.opacity = '0';
      item.style.padding = '0';
    }, 0);

    setTimeout(() => item.remove(), 300);
  }

  showEmptyCartMessage() {
    if (this.drawerItems) {
      this.drawerItems.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8">
          <p class="mb-4 font-serif">Your cart is empty</p>
   
        </div>
      `;
    }
  }
}

class ProductForm {
  constructor(container) {
    // Initialize properties
    this.container = container;
    this.productId = container.dataset.productId;
    this.form = container.querySelector(`#product-form-${this.productId}`);
    this.variantSelects = container.querySelectorAll('.variant-select');
    this.quantityInput = container.querySelector('[data-quantity-input]');
    this.variantSelect = container.querySelector('[data-product-select]');
    this.addToCartButton = container.querySelector('.add-to-cart-button');
    this.currentVariant = null;
    this.currentPrice = 0;
    this.productData = null;

    window.shopSettings = window.shopSettings || {
      moneyFormat: '{{ shop.money_format | json }}', // This will be replaced by Shopify with the actual format
    };
    // Initialize
    this.init();
  }

  async init() {
    await this.initializeProductData();
    this.initializeListeners();
  }

  initializeListeners() {
    // Variant selectors
    this.variantSelects.forEach((select) => {
      select.addEventListener('change', this.onVariantChange.bind(this));
    });

    // Quantity controls
    this.container.querySelectorAll('[data-action]').forEach((button) => {
      button.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    // Quantity input direct changes
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.updateButtonPrice.bind(this));
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  async initializeProductData() {
    try {
      this.productData = await this.getProductJson();
      if (this.productData) {
        // Look for selected variant first
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');

        if (variantId) {
          this.currentVariant = this.productData.variants.find((v) => v.id.toString() === variantId);
        }

        // Fallback to first available or first variant
        if (!this.currentVariant) {
          this.currentVariant = this.productData.selected_or_first_available_variant || this.productData.variants[0];
        }

        if (this.currentVariant) {
          this.currentPrice = this.currentVariant.price;
          // Ensure price is updated immediately on initialization
          setTimeout(() => this.updateButtonPrice(), 0);
        }
      }
    } catch (error) {
      console.error('Error initializing product data:', error);
    }
  }
  async getProductJson() {
    try {
      const response = await fetch(`${window.location.pathname}.js`);
      if (!response.ok) throw new Error('Failed to fetch product data');
      return await response.json();
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  }

  updateButtonPrice() {
    if (!this.addToCartButton || !this.currentVariant) return;

    try {
      const buttonText = this.addToCartButton.querySelector('.add-to-cart-text');
      const priceElement = buttonText.querySelector('.form-price');
      const quantity = parseInt(this.quantityInput?.value || 1);

      if (buttonText) {
        if (this.currentVariant.available) {
          // Get the base unit price (already formatted with currency)
          const basePrice = this.currentVariant.price / 100;
          // Calculate total based on quantity
          const total = basePrice * quantity;

          // Format the price with the same format as the money filter
          const formattedPrice = this.formatMoney(total);

          // Update price in button
          if (priceElement) {
            priceElement.textContent = formattedPrice;
          } else {
            // If price element doesn't exist, create it
            const newPriceSpan = document.createElement('span');
            newPriceSpan.className = 'form-price';
            newPriceSpan.textContent = formattedPrice;

            // Insert price before "Add to Bag" text
            buttonText.textContent = '';
            buttonText.appendChild(newPriceSpan);
            buttonText.appendChild(document.createTextNode(' - Add'));

            const bagText = document.createElement('span');
            bagText.className = 'hidden lg:inline';
            bagText.textContent = ' to Bag';
            buttonText.appendChild(bagText);
          }
        } else {
          buttonText.textContent = 'Sold Out';
        }
      }
    } catch (error) {
      console.error('Error updating button text:', error);
    }
  }

  formatMoney(amount) {
    // Get the base price format from the button's data attribute
    const baseFormat = this.addToCartButton.dataset.price;
    if (!baseFormat) return `€${amount.toFixed(2)}`;

    // Extract the currency symbol
    const currencySymbol = baseFormat
      .replace(/[0-9.,]/g, '')
      .trim()
      .split(' ')[0];

    // Format the amount with 2 decimal places
    const formattedAmount = amount.toFixed(2);

    // Return in €XX.XX format
    return `${currencySymbol}${formattedAmount}`;
  }
  // 2. Update the quantity change handler
  handleQuantityChange(event) {
    const button = event.currentTarget;
    const action = button.dataset.action;
    const input = this.quantityInput;

    if (!input) return;

    const currentValue = parseInt(input.value) || 1;
    let newValue = currentValue;

    if (action === 'increase') {
      newValue = currentValue + 1;
    } else if (action === 'decrease' && currentValue > 1) {
      newValue = currentValue - 1;
    }

    input.value = Math.max(1, newValue);

    // Update price immediately when quantity changes
    this.updateButtonPrice();

    // Dispatch change event for other listeners
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  findVariant(selectedOptions) {
    return this.productData.variants.find((variant) => {
      return selectedOptions.every((option, index) => {
        return variant[`option${index + 1}`] === option;
      });
    });
  }

  onVariantChange() {
    try {
      const selectedOptions = Array.from(this.variantSelects).map((select) => select.value);
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.currentVariant = variant;
        this.currentPrice = variant.price;
        this.updateVariantSelect(variant);
        this.updateAddToCartButton(variant);
        this.updateURL(variant);
        this.updateButtonPrice();
      }
    } catch (error) {
      console.error('Error changing variant:', error);
    }
  }

  updateVariantSelect(variant) {
    if (this.variantSelect) {
      this.variantSelect.value = variant.id;
    }
  }

  updateAddToCartButton(variant) {
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !variant.available;
      this.currentVariant = variant;
      this.updateButtonPrice();
      this.addToCartButton.dataset.productId = variant.id;
    }
  }

  updateURL(variant) {
    if (variant && window.history && window.history.replaceState) {
      const newUrl = `${window.location.pathname}?variant=${variant.id}`;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }

  updatePrice(variant) {
    const priceContainer = this.container.querySelector('.product-price');
    if (priceContainer && variant) {
      priceContainer.innerHTML = `
        <span>${variant.price}</span>
        ${
          variant.compare_at_price > variant.price
            ? `<span class="text-gray-500 line-through ml-2">${variant.compare_at_price}</span>`
            : ''
        }
      `;
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.addToCartButton) return;

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);

    try {
      this.toggleLoadingState(true);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error('Add to bag failed');

      const cartData = await response.json();

      // Get fresh cart data
      const updatedCart = await fetch('/cart.js').then((res) => res.json());

      // Dispatch cart update event
      document.dispatchEvent(
        new CustomEvent('cart:added', {
          detail: { cartData: updatedCart },
        })
      );

      // Reset the form state
      this.toggleLoadingState(false);
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(this.getErrorMessage(error));
      this.toggleLoadingState(false);
    }
  }
  toggleLoadingState(isLoading) {
    if (!this.addToCartButton) return;

    const textElement = this.addToCartButton.querySelector('.add-to-cart-text');
    const spinnerElement = this.addToCartButton.querySelector('.loading-spinner');
    this.addToCartButton.disabled = isLoading;

    if (isLoading) {
      textElement.textContent = 'Adding...';
      spinnerElement?.classList.remove('hidden');
    } else {
      // Reset quantity to 1
      if (this.quantityInput) {
        this.quantityInput.value = '1';
      }

      // Force rebuild the button content
      if (textElement) {
        textElement.innerHTML = ''; // Clear existing content

        // Create and append price span
        const priceSpan = document.createElement('span');
        priceSpan.className = 'form-price';
        textElement.appendChild(priceSpan);

        // Add the separator and base text
        textElement.appendChild(document.createTextNode(' - Add'));

        // Add "to Bag" with responsive class
        const bagSpan = document.createElement('span');
        bagSpan.className = 'hidden lg:inline';
        bagSpan.textContent = ' to Bag';
        textElement.appendChild(bagSpan);
      }

      spinnerElement?.classList.add('hidden');

      // Update the price display
      this.updateButtonPrice();
    }
  }
  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50 animate-fade-in';
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);
    setTimeout(() => errorDiv.remove(), 3000);
  }

  async validateInventory(productId, requestedQuantity) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      const product = await response.json();
      const variant = product.variants.find((v) => v.id === parseInt(productId, 10));

      if (!variant) {
        throw new Error('Product variant not found');
      }

      if (!variant.available) {
        throw new Error('Product is sold out');
      }

      if (
        variant.inventory_management === 'shopify' &&
        variant.inventory_policy === 'deny' &&
        variant.inventory_quantity < requestedQuantity
      ) {
        throw new Error(`Not enough inventory. Only ${variant.inventory_quantity} available`);
      }

      return true;
    } catch (error) {
      console.error('Inventory validation error:', error);
      throw error;
    }
  }
}

// Initialize all features when the page loads
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
  document.querySelectorAll('.product-form').forEach((container) => {
    // Store the ProductForm instance on the element
    container.__productForm = new ProductForm(container);
  });
});
